import { useEffect, useState } from "react"
import classNames from "classnames"
import PropTypes from "prop-types"
import { useApolloClient } from "@apollo/react-hooks"

import { useTranslation } from "lib/i18n/translation"
import { useModal } from "lib/modal"
import { useVideoPlayer } from "lib/video-player"
import { useAuth } from "lib/auth"
import { useAccesses } from "lib/accesses"
import { useCountry } from "lib/country"

import PlaylistAdd from "components/modals/playlist-add"
import { FilmOverlay } from "components/modals/svod/film-overlay"
import { download } from "components/common/product-access-link"

export const Actions = ({ product, isSingle, contextClassName = "preview" }) => {
	const { c } = useTranslation()
	const { show } = useModal()
	const {	inEuro } = useCountry()
	const { user, showLoginSvod } = useAuth()
	const { hasSubscribed } = useAccesses()
	const { seeExtract, seeFilm } = useVideoPlayer()
	const client = useApolloClient()

	const [actions, setActions] = useState([])

	const handleWatchMovie = event => {
		event.preventDefault()
		if (hasSubscribed) {
			seeFilm(product)

			return
		}
		showLoginSvod()
	}

	const handleSeeTrailer = event => {
		event.preventDefault()
		seeExtract(product)
	}

	const handleProductModal = event => {
		event.preventDefault()
		show({
			id: "modal-film",
			children: <FilmOverlay product={product} />,
			className: "modal film standalone stackable expanded",
			isPreview: true
		})
	}

	const handleAddToPlaylist = event => {
		event.preventDefault()
		if (!user) {
			return
		}

		show({
			id: "playlist",
			children: <PlaylistAdd productId={product.id} />,
			className: "modal short"
		})
	}

	useEffect(
		() => {
			const actions = []

			if (isSingle) {
				actions.push({
					key: "watch-the-movie",
					title: c("watch-the-movie"),
					className: "icon-play alt",
					onClick: handleWatchMovie
				})
			}

			if (hasSubscribed
				&& inEuro
			) {
				actions.push({
					key: "download-movie",
					title: c("download-movie"),
					className: "icon-download",
					onClick: event => download(event, product.id, client)
				})
			}

			if (product.extractsCount > 0 || product.trailersCount > 0) {
				actions.push({
					key: "see-trailer",
					title: product.trailersCount > 0 ? c("see-trailer") : c("see-extract"),
					className: "icon-reel",
					onClick: handleSeeTrailer
				})
			}

			if (!isSingle) {
				actions.push({
					key: "see-single-film",
					title: c("see-single-film"),
					className: "icon-file",
					onClick: handleProductModal
				})
			}

			actions.push({
				key: "add-to-my-playlist",
				title: c("add-to-my-playlist"),
				className: classNames("add-to-playlist", { "disabled": !user }),
				onClick: handleAddToPlaylist
			})

			setActions(actions)
		},
		[isSingle, hasSubscribed, product]
	)

	return (
		<nav
			className={classNames(
				`${contextClassName}-actions`,
				{
					"large buttons rounded": isSingle
				}
			)}
			aria-label="actions film"
		>
			{actions.map(action => (
				<a
					href="#"
					rel="nofollow noopener"
					{...action}
				>
					{action.title}
				</a>
			))}
		</nav>
	)
}

Actions.propTypes = {
	product: PropTypes.object.isRequired,
	isSingle: PropTypes.bool,
	contextClassName: PropTypes.oneOf(["preview", "film"])
}
